import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '$hooks';

type Props = {
    initial?: number;
    min?: number;
    max?: number;
    onUpdate?: (value: number) => void;
    isDisabled?: boolean;
    multiplyValue?: number;
    appStyle?: boolean;
    disableInfoMessage?: boolean;
};

const buttonHeight = '50px';
const buttonWidth = '40px';
const smallButtonSize = '32px';

export const Counter: FC<Props> = (props: Props): JSX.Element => {
    const {
        isDisabled,
        initial,
        min = 1,
        max = Number.MAX_SAFE_INTEGER,
        multiplyValue = 1,
        appStyle,
        onUpdate,
        disableInfoMessage,
    } = props;
    const { translate } = useTranslation();

    const [value, setValue] = useState<number>(initial || 1);
    const [editableValue, setEditableValue] = useState<number>(initial || 1);

    const onValueChange = (typedValue: number | string) => {
        if (typedValue < min || typedValue > max || typedValue === value) {
            setEditableValue(value);
            return;
        }

        const newValue = typeof typedValue === 'number' ? typedValue : +typedValue;
        if (isNaN(newValue)) {
            setEditableValue(value);
            return;
        }

        if (newValue === value) {
            return;
        }
        setValue(newValue);
        setEditableValue(newValue);
        onUpdate && onUpdate(newValue);
    };

    const incrementClicked = () => {
        if (value >= max) {
            return;
        }
        const newValue = increment(value, multiplyValue);
        if (newValue === value) {
            return;
        }

        setValue(newValue);
        setEditableValue(newValue);
        onUpdate && onUpdate(newValue);
    };

    const decrementClicked = () => {
        if (value <= min) {
            return;
        }
        const newValue = decrement(value, multiplyValue);
        if (newValue === value) {
            return;
        }
        setValue(newValue);
        setEditableValue(newValue);
        onUpdate && onUpdate(newValue);
    };

    const increment = (value: number, multiplyValue: number): number => {
        const calculatedMax = max ? max : Number.MAX_SAFE_INTEGER;
        return calculatedMax <= value + multiplyValue ? calculatedMax : value + multiplyValue;
    };

    const decrement = (value: number, multiplyValue: number): number => {
        return value - multiplyValue <= min ? min : value - multiplyValue;
    };

    const infoMessage = () => {
        if (value <= min) {
            return `${translate('productItem.productRowItem.lblMinimumOrder')} ${min} ${
                (multiplyValue > 1 && multiplyValue) || ''
            }`;
        }
        if (value >= max) {
            return `${translate('productItem.productRowItem.lblMaximumOrder')} ${max} ${
                (multiplyValue > 1 && multiplyValue) || ''
            }`;
        }
        return '';
    };

    return (
        <CounterContainer>
            <CounterButton
                type="button"
                onClick={decrementClicked}
                appStyle={appStyle}
                disabled={isDisabled || value <= min}
            >
                -
            </CounterButton>
            {!disableInfoMessage && value <= min && (
                <MessageBoxContainer>
                    <MessageBoxContent>{infoMessage()}</MessageBoxContent>
                    <MessageBoxTriangle />
                </MessageBoxContainer>
            )}
            <CounterValue
                type="tel"
                pattern="[0-9]*"
                inputMode="numeric"
                onChange={(e) => setEditableValue(Number(e.target.value))}
                onBlur={(e) => onValueChange(e.target.value)}
                value={editableValue}
                appStyle={appStyle}
                disabled={isDisabled}
            />
            <CounterButton
                increment
                type="button"
                onClick={incrementClicked}
                appStyle={appStyle}
                disabled={isDisabled || value >= max}
            >
                +
            </CounterButton>
            {!disableInfoMessage && value >= max && (
                <MessageBoxContainer>
                    <MessageBoxContent>{`${translate(
                        'productItem.productRowItem.lblMaximumOrder'
                    )} ${max} ${multiplyValue}`}</MessageBoxContent>
                    <MessageBoxTriangle />
                </MessageBoxContainer>
            )}
        </CounterContainer>
    );
};

const CounterButton = styled.button<{ increment?: boolean; appStyle?: boolean }>(({ theme, increment, appStyle }) => ({
    height: appStyle ? smallButtonSize : buttonHeight,
    width: appStyle ? smallButtonSize : buttonWidth,
    cursor: 'pointer',
    outline: 'none',
    border: theme.general.border,
    borderRight: increment ? theme.general.border : 'none',
    borderLeft: increment ? 'none' : theme.general.border,
    borderTopLeftRadius: increment ? 'none' : theme.general.borderRadius,
    borderBottomLeftRadius: increment ? 'none' : theme.general.borderRadius,
    borderTopRightRadius: increment ? theme.general.borderRadius : 'none',
    borderBottomRightRadius: increment ? theme.general.borderRadius : 'none',
    backgroundColor: theme.colors.white,

    '&:hover +': {
        [`${MessageBoxContainer}`]: {
            display: 'block',
        },
    },

    '&:disabled': {
        opacity: 0.5,
    },
}));

const CounterValue = styled.input<{ appStyle?: boolean }>(({ theme, appStyle }) => ({
    height: appStyle ? smallButtonSize : buttonHeight,
    width: appStyle ? smallButtonSize : buttonWidth,
    border: theme.general.border,
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box',
    padding: 0,
    textAlign: 'center',

    '&:disabled': {
        opacity: 0.5,
    },
}));

const MessageBoxContainer = styled.div({
    position: 'absolute',
    display: 'none',
    width: '100%',
    bottom: buttonHeight,
});

const MessageBoxContent = styled.div(({ theme }) => ({
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '200px',
    height: '40px',
    bottom: '10px',
    textAlign: 'center',
    fontSize: theme.fontSizes.sm,
    borderRadius: '5px',
    backgroundColor: theme.colors.primaryDark,
    color: theme.colors.white,
}));

const MessageBoxTriangle = styled.div(({ theme }) => ({
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderTop: `10px solid ${theme.colors.primaryDark}`,
}));

const CounterContainer = styled.div({
    position: 'relative',
    display: 'flex',
});
